import {CopyText, E} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, Column, Row, Text, TextInput} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {ScrollView} from "react-vscrollview";
import {InfoButton, RunInAction, RunInAction_Set} from "web-vcore";
import {LiveSession} from "../../../../Engine/LiveSession.js";
import {GetAllEngineSessions, NotifyLiveSessionLogChanged} from "../../../../Engine/LiveSessionManagement.js";
import {store} from "../../../../Store/index.js";
import {JourneyTab} from "../../../../Store/main/tools/journey.js";
import {InAndroid} from "../../../../Utils/Bridge/Bridge_Native.js";
import {ReturnBar_Android} from "../../../@Shared/ReturnBar_Android.js";
import {LogEntry, LogType} from "../LogEntry.js";
import {SessionlessLogPeriod, sessionlessLogPeriods} from "./SessionLog.js";

export const SessionLogPanel = observer_mgl(()=>{
	const uiState = store.main.engine.config; // todo: move these fields to a store location shared by both session-types
	const logTypesToShow = uiState.logTypesToShow;
	const tracker1 = uiState.liveSession_setOrLoggedAt;

	const allEngineSessions = GetAllEngineSessions().OrderBy(a=>a.coreData.startTime);
	let sessionsAndLogPeriods = ([] as (LiveSession|SessionlessLogPeriod)[]).concat(allEngineSessions).concat(sessionlessLogPeriods);
	sessionsAndLogPeriods = sessionsAndLogPeriods.OrderBy(a=>a instanceof LiveSession ? a.coreData.startTime : a.startTime);
	//const logString = GetLogString(sessionsAndLogPeriods, logTypesToShow);

	const setShowLogType = (logType: LogType, show: boolean)=>{
		RunInAction("SessionLogPanel.setShowLogType", ()=>{
			uiState.logTypesToShow = show ? logTypesToShow.concat(logType) : logTypesToShow.Exclude(logType);
		});
	};

	function IsFilterTextRegex(filterText: string) { return filterText.startsWith("/") && filterText.endsWith("/"); }
	function DoesFilterTextMatch(filterText: string, str: string) {
		const regex = IsFilterTextRegex(filterText);
		if (regex) {
			try {
				return new RegExp(filterText.slice(1, -1)).test(str);
			} catch (ex) {
				return false;
			}
		}
		return str.includes(filterText);
	}
	function ShouldLogEntryBeShown(entry: LogEntry) {
		if (!logTypesToShow.Contains(entry.type)) return false;
		//const entryStr = entry.message;
		const entryStr = entry.toString();
		if (uiState.include_enabled && uiState.include_text && !DoesFilterTextMatch(uiState.include_text, entryStr)) return false;
		if (uiState.exclude_enabled && uiState.exclude_text && DoesFilterTextMatch(uiState.exclude_text, entryStr)) return false;
		return true;
	}
	function FilterLogEntries(entries: LogEntry[]) {
		return entries.filter(a=>ShouldLogEntryBeShown(a));
	}
	function GetLogEntriesAsString(filterEntries: boolean) {
		return sessionsAndLogPeriods.map(group=>{
			const headerText = GetSessionOrLogPeriodHeaderText(group);
			const entriesToShow = filterEntries ? FilterLogEntries(group.logEntries) : group.logEntries;
			return `${headerText}\n==========\n${entriesToShow.map((entry, index)=>{
				return entry.toString(entriesToShow[index - 1]);
			}).join("\n")}`;
		}).join("\n\n\n\n");
	}

	const gap = InAndroid(0) ? 3 : 7;
	const fontSize = InAndroid(0) ? 11 : undefined;
	return (
		<>
			{/*<ReturnBar_Android text="Log" onBack={()=>RunInAction_Set(()=>store.main.engine.journey.tab = JourneyTab.engine)}/>*/}
			<Row mb={5} center style={{flexWrap: "wrap", gap}}>
				<Row style={{fontSize: 13, height: 50, flexWrap: "wrap", gap}}>
					<Text>Types:</Text>
					<CheckBox style={{fontSize}} text="Actions" value={logTypesToShow.Contains(LogType.Action)} onChange={val=>setShowLogType(LogType.Action, val)}/>
					<CheckBox style={{fontSize}} text="Events (large)" value={logTypesToShow.Contains(LogType.Event_Large)} onChange={val=>setShowLogType(LogType.Event_Large, val)}/>
					<CheckBox style={{fontSize}} text="Events (small)" value={logTypesToShow.Contains(LogType.Event_Small)} onChange={val=>setShowLogType(LogType.Event_Small, val)}/>
					<CheckBox style={{fontSize}} text="App log" value={logTypesToShow.Contains(LogType.App_Log)} onChange={val=>setShowLogType(LogType.App_Log, val)}/>
					<CheckBox style={{fontSize}} text="App error" value={logTypesToShow.Contains(LogType.App_Error)} onChange={val=>setShowLogType(LogType.App_Error, val)}/>
				</Row>
				<Column style={{flex: 1, height: 50}}>
					<Row center>
						<CheckBox style={{fontSize, width: 75}} text="Include:" value={uiState.include_enabled} onChange={val=>RunInAction_Set(()=>uiState.include_enabled = val)}/>
						<InfoButton ml={3} text="If enabled, log entries not containing the given text are hidden. (for regex: /.../)"/>
						<TextInput ml={5} style={{flex: 1}} enabled={uiState.include_enabled}
							value={uiState.include_text} onChange={val=>RunInAction_Set(()=>uiState.include_text = val)}/>
					</Row>
					<Row center>
						<CheckBox style={{fontSize, width: 75}} text="Exclude:" value={uiState.exclude_enabled} onChange={val=>RunInAction_Set(()=>uiState.exclude_enabled = val)}/>
						<InfoButton ml={3} text="If enabled, log entries containing the given text are hidden. (for regex: /.../)"/>
						<TextInput ml={5} style={{flex: 1}} enabled={uiState.exclude_enabled}
							value={uiState.exclude_text} onChange={val=>RunInAction_Set(()=>uiState.exclude_text = val)}/>
					</Row>
				</Column>
				<Column style={{height: 50}}>
					<Row>
						<Text>Copy:</Text>
						<Button ml={5} text="All" p="3px 10px" style={{fontSize: 12}} onClick={()=>{
							CopyText(GetLogEntriesAsString(false));
						}}/>
						<Button ml={3} text="Filtered" p="3px 10px" style={{fontSize: 12}} onClick={()=>{
							CopyText(GetLogEntriesAsString(true));
						}}/>
					</Row>
					<Row mt="auto">
						<Text>Clear:</Text>
						<Button ml={5} text="All" p="3px 10px" style={{fontSize: 12}} onClick={()=>{
							ShowMessageBox({
								title: "Clear log?", cancelButton: true,
								message: "Clear all messages in the log?",
								onOK: async()=>{
									for (const session of allEngineSessions) {
										//session.ClearLog();
										session.logEntries.Clear(); // call this instead of ClearLog, so we call the Notify... function only once (see below)
									}
									for (const period of sessionlessLogPeriods) {
										period.logEntries.Clear();
									}
									// call these once at end
									NotifyLiveSessionLogChanged();
								},
							});
						}}/>
					</Row>
				</Column>
			</Row>
			{/*<TextArea style={{flex: 1}} editable={false} value={logString}/>*/}
			<ScrollView>
				{sessionsAndLogPeriods.map((group, groupIndex)=>{
					const headerText = GetSessionOrLogPeriodHeaderText(group);
					const entriesToShow = FilterLogEntries(group.logEntries);
					return <Column sel key={groupIndex} style={{wordBreak: "break-word"}}>
						<Row key={groupIndex} mt={groupIndex == 0 ? 0 : 100} style={{paddingBottom: "7px", borderBottom: "3px solid rgba(255,255,255,.7)", fontSize: 18, fontWeight: "bold"}}>
							{headerText}
						</Row>
						{entriesToShow.map((entry, index)=>{
							return <LogEntryUI key={index} entry={entry} index={index} entries={entriesToShow}/>;
						})}
					</Column>;
				})}
			</ScrollView>
		</>
	);
});

function GetSessionOrLogPeriodHeaderText(group: LiveSession|SessionlessLogPeriod) {
	let headerText: string;
	if (group instanceof LiveSession) {
		const typeStr = {LiveSession_Local: "local", LiveSession_Client: "client"}.Pairs().find(a=>group.constructor.name == a.key)!.value;
		//result += `========== Start of session (${Moment(session.startTime).format("YYYY-MM-DD HH:mm:ss")}) ==========\n`;
		headerText = `Start of engine-session (${typeStr})`;
	} else {
		headerText = `Start of sessionless log-period`;
	}
	return headerText;
}

const LogEntryUI = observer_mgl((props: {entry: LogEntry, index: number, entries: LogEntry[]})=>{
	const {entry, index, entries} = props;
	const text = entry.toString(entries[index - 1]);
	return (
		<Row mt={10} style={E(
			{color: "rgba(255,255,255,.7)"},
			//index % 2 == 0 && {backgroundColor: HSLA(0, 0, 1, .2)},
			//index % 2 != 0 && {backgroundColor: HSLA(0, 0, 0, .3)},
		)}>
			{text}
		</Row>
	);
});